/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Users, Shield, Search, ChevronDown, Key, UserCheck, 
  MapPin, Check, X, ShieldAlert, Edit2, CheckCircle2, Crown
} from 'lucide-react';
import { isUserOrgLead } from '../../utils/orgTeamUtils';
import { StudentAvatar } from '../Common/StudentAvatar';
import { HeroBanner } from '../Common/HeroBanner';
import { PasswordManagementModal } from '../Common/PasswordManagementModal';
import { UserProfile, Tenant } from '../../types';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { useInAppDialog } from '../Common/InAppDialog';

export const RegionalUsersPage: React.FC = () => {
  const { users, tenants, updateUserProfile, activeUser, activeTenant, schools, setUserAsOrgLead, syncExistingOrgUsersToLeads } = useWBL();
  const { showAlert, showToast } = useInAppDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  
  // Track which user is currently being edited for their region association
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [selectedPasswordUser, setSelectedPasswordUser] = useState<UserProfile | null>(null);

  const toggleSchoolAssociation = async (user: UserProfile, targetSchoolId: string) => {
    setSavingUid(user.uid);
    try {
      const currentSchools = user.schoolsServedIds || [];
      const primarySchoolId = user.schoolId;
      
      let nextSchoolsServed: string[];

      if (primarySchoolId === targetSchoolId) {
        // Toggling OFF the primary school
        if (currentSchools.length > 0) {
          const nextPrimaryId = currentSchools[0];
          const nextSchoolsServedList = currentSchools.filter(id => id !== nextPrimaryId);
          const newPrimarySchool = schools.find(s => s.id === nextPrimaryId);
          
          // Also update the name-based lists for legacy compatibility
          const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
            const found = schools.find(s => s.id === id);
            return found ? found.name : id;
          });

          await updateUserProfile(user.uid, {
            schoolId: nextPrimaryId,
            school: newPrimarySchool?.name || '',
            schoolsServedIds: nextSchoolsServedList,
            schoolsServed: nextSchoolsServedNames
          });
        } else {
          // Unassign completely
          await updateUserProfile(user.uid, {
            schoolId: '',
            school: '',
            schoolsServedIds: [],
            schoolsServed: []
          });
        }
      } else if (currentSchools.includes(targetSchoolId)) {
        // Toggling OFF an additional school
        const nextSchoolsServedList = currentSchools.filter(id => id !== targetSchoolId);
        const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
          const found = schools.find(s => s.id === id);
          return found ? found.name : id;
        });
        
        await updateUserProfile(user.uid, { 
          schoolsServedIds: nextSchoolsServedList,
          schoolsServed: nextSchoolsServedNames
        });
      } else {
        // Toggling ON a school
        if (!primarySchoolId) {
          const newPrimarySchool = schools.find(s => s.id === targetSchoolId);
          await updateUserProfile(user.uid, {
            schoolId: targetSchoolId,
            school: newPrimarySchool?.name || ''
          });
        } else {
          const nextSchoolsServedList = [...currentSchools, targetSchoolId];
          const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
            const found = schools.find(s => s.id === id);
            return found ? found.name : id;
          });

          await updateUserProfile(user.uid, { 
            schoolsServedIds: nextSchoolsServedList,
            schoolsServed: nextSchoolsServedNames
          });
        }
      }
    } catch (err) {
      console.error("Failed to update school association:", err);
    } finally {
      setSavingUid(null);
    }
  };

  // Global scope check: activeTenant is 'global', user tenant is 'global', or user role is superadmin
  const isGlobal = activeTenant?.id === 'global' || activeUser?.tenantId === 'global' || activeUser?.role === 'superadmin';

  // Identify regions this administrator has jurisdiction over
  const adminRegions = useMemo(() => {
    if (!activeUser) return [];
    if (isGlobal) return ['all', 'global', ...(tenants || []).map(t => t.id)];
    const regions = [activeUser.tenantId, ...(activeUser.extendedRegions || [])];
    return Array.from(new Set(regions.filter(Boolean)));
  }, [activeUser, isGlobal, tenants]);

  // Allowed tenants inside the admin's scope
  const allowedTenants = useMemo(() => {
    if (isGlobal) return tenants || [];
    return (tenants || []).filter(t => adminRegions.includes(t.id));
  }, [tenants, adminRegions, isGlobal]);

  // Filter users who belong to the administered regions (or all users if Global is selected)
  const regionalUsers = useMemo(() => {
    return (users || []).filter(u => {
      if (isGlobal) return true;
      // Don't show superadmins to regional admins for editing/viewing
      if (u.role === 'superadmin') return false;

      // Check if user is associated with any of the admin's regions
      const userRegions = [u.tenantId, ...(u.extendedRegions || [])].filter(Boolean);
      return userRegions.some(r => adminRegions.includes(r));
    });
  }, [users, adminRegions, isGlobal]);

  // Filtered users for table display based on search and filters
  const filteredUsers = useMemo(() => {
    const list = regionalUsers.filter(u => {
      // Search matches (Name, email, school, organizationName, role)
      const nameMatch = (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (`${u.contactFirstName || ''} ${u.contactLastName || ''}`).toLowerCase().includes(searchQuery.toLowerCase());
      const emailMatch = (u.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      const schoolMatch = (u.school || '').toLowerCase().includes(searchQuery.toLowerCase());
      const orgMatch = (u.orgName || u.organizationName || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSearch = nameMatch || emailMatch || schoolMatch || orgMatch;

      // Role filter matches
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;

      // Region filter matches
      const userRegions = [u.tenantId, ...(u.extendedRegions || [])].filter(Boolean);
      const matchesRegion = regionFilter === 'all' || (isGlobal && regionFilter === 'global') || userRegions.includes(regionFilter);

      return matchesSearch && matchesRole && matchesRegion;
    });

    return sortStudentsByLastName(list, (u: UserProfile) => u.name, (u: UserProfile) => u);
  }, [regionalUsers, searchQuery, roleFilter, regionFilter, isGlobal]);

  const toggleRegionAssociation = async (user: UserProfile, targetTenantId: string) => {
    setSavingUid(user.uid);
    try {
      const currentRegions = user.extendedRegions || [user.tenantId];
      let nextRegions: string[];
      
      if (currentRegions.includes(targetTenantId)) {
        // If they want to remove it, ensure they don't remove their primary tenantId
        if (user.tenantId === targetTenantId) {
          // If they have other extended regions, we can shift their primary, or prevent removal
          const remaining = currentRegions.filter(id => id !== targetTenantId);
          if (remaining.length > 0) {
            nextRegions = remaining;
            await updateUserProfile(user.uid, { 
              tenantId: remaining[0], 
              extendedRegions: remaining 
            });
          } else {
            // Cannot remove the only remaining region
            showAlert({
              title: "Region Required",
              message: "A user must be assigned to at least one active region.",
              type: "warning"
            });
            return;
          }
        } else {
          nextRegions = currentRegions.filter(id => id !== targetTenantId);
          await updateUserProfile(user.uid, { extendedRegions: nextRegions });
        }
      } else {
        nextRegions = [...currentRegions, targetTenantId];
        await updateUserProfile(user.uid, { extendedRegions: nextRegions });
      }
    } catch (err) {
      console.error("Failed to update user regions:", err);
    } finally {
      setSavingUid(null);
    }
  };

  // Helper to resolve role color theme classes
  const getRoleBadgeClasses = (role: string) => {
    switch (role) {
      case 'advisor':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'organization':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'student':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'parent':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'regional_admin':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  // Metric summaries
  const totalCount = regionalUsers.length;
  const advisorsCount = regionalUsers.filter(u => u.role === 'advisor').length;
  const orgsCount = regionalUsers.filter(u => u.role === 'organization').length;
  const studentsCount = regionalUsers.filter(u => u.role === 'student').length;

  return (
    <div className="space-y-6 font-sans animate-fade-in">
      <HeroBanner 
        title="USER DIRECTORY"
        subtitle={`REGIONAL MANAGEMENT OVERSEEING ${allowedTenants.length} ASSIGNED DISTRICTS/REGIONS`}
        pageKey="opportunities"
      />

      <div className="w-[95%] mx-auto space-y-8 pb-12">
                {/* Statistics Widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center space-x-4">
            <div className="p-3 bg-sky-50 text-sky-600 rounded-xl">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider font-mono">Total Users</p>
              <h3 className="text-2xl font-black text-slate-800 font-mono mt-0.5">{totalCount}</h3>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center space-x-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <UserCheck className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider font-mono">Students</p>
              <h3 className="text-2xl font-black text-slate-800 font-mono mt-0.5">{studentsCount}</h3>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center space-x-4">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider font-mono">WBL Advisors</p>
              <h3 className="text-2xl font-black text-slate-800 font-mono mt-0.5">{advisorsCount}</h3>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center space-x-4">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Key className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider font-mono">Employer Partners</p>
              <h3 className="text-2xl font-black text-slate-800 font-mono mt-0.5">{orgsCount}</h3>
            </div>
          </div>
        </div>

        {/* Directory Controls & Table */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden flex flex-col">
          {/* Header Controls */}
          <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <h2 className="text-lg font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
                <Shield className="w-5 h-5 text-[#0094c8]" />
                <span>Regional Authority Ledger</span>
              </h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-mono">
                RBAC Profile Provisioning & Regional Alignment Protocol
              </p>
            </div>

            {/* Filter Suite */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Bar */}
              <div className="relative min-w-[240px] flex-1 lg:flex-initial">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, org..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-50/50 border border-slate-200 outline-none rounded-xl py-2 pl-9 pr-4 focus:ring-1 focus:ring-custom-primary font-medium text-xs"
                />
              </div>

              {/* Role Filter */}
              <div className="relative">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 pr-8 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:ring-2 focus:ring-[#0094c8] appearance-none font-bold uppercase tracking-wider font-mono"
                >
                  <option value="all">ALL ROLES</option>
                  <option value="student">STUDENTS</option>
                  <option value="advisor">ADVISORS</option>
                  <option value="organization">EMPLOYERS</option>
                  <option value="parent">PARENTS</option>
                  <option value="regional_admin">REGIONAL ADMINS</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-3 h-3 text-slate-400 pointer-events-none" />
              </div>

              {/* Region Filter */}
              <div className="relative">
                <select
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  className="px-3 py-2 pr-8 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:ring-2 focus:ring-[#0094c8] appearance-none font-bold uppercase tracking-wider font-mono"
                >
                  <option value="all">ALL REGIONS</option>
                  {allowedTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name.toUpperCase()}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-3 w-3 h-3 text-slate-400 pointer-events-none" />
              </div>

              <button
                type="button"
                onClick={async () => {
                  const updatedCount = await syncExistingOrgUsersToLeads();
                  showToast({
                    title: "Organization Leads Synced",
                    message: updatedCount > 0
                      ? `Updated ${updatedCount} existing organization user(s) to Organization Lead.`
                      : "All existing organization users are already synced with Lead status.",
                    type: "success"
                  });
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-black uppercase tracking-wider cursor-pointer transition-all"
              >
                <Crown className="w-3.5 h-3.5 text-amber-600" />
                <span>Sync Existing Org Leads</span>
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            {filteredUsers.length === 0 ? (
              <div className="py-16 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h4 className="text-sm font-bold text-slate-700 uppercase">No users found</h4>
                <p className="text-xs text-slate-400 mt-1">Refine your search parameters or select a different regional filter.</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 tracking-wider">
                    <th className="py-4 px-6">User Identity</th>
                    <th className="py-4 px-6">Assigned System Role</th>
                    <th className="py-4 px-6">Designated Schools</th>
                    <th className="py-4 px-6">Authorized Regions</th>
                    <th className="py-4 px-6 text-right">Security</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                  {filteredUsers.map(user => {
                    const authorizedRegions = user.extendedRegions || [user.tenantId];

                    return (
                      <tr key={user.uid} className="hover:bg-slate-50/50 transition-colors">
                        {/* Identity */}
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-slate-100 bg-slate-50">
                              <StudentAvatar 
                                studentId={user.uid} 
                                studentName={user.name} 
                                studentEmail={user.email} 
                                profile={user}
                              />
                            </div>
                            <div>
                              <h4 className="font-extrabold text-slate-800 text-sm">
                                {formatLastNameFirst(user.name, user)}
                              </h4>
                              <p className="text-[10px] text-slate-400 font-mono mt-0.5">{user.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* System Role */}
                        <td className="py-4 px-6 text-left">
                          <span className={`px-2.5 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider ${getRoleBadgeClasses(user.role)}`}>
                            {user.role === 'organization' ? 'employer' : user.role}
                          </span>
                        </td>

                        {/* Designated Schools (for advisors) / Affiliation for others */}
                        <td className="py-4 px-6">
                          {user.role === 'advisor' ? (
                            <div className="flex flex-col space-y-1.5 max-w-[300px]">
                              {(schools || [])
                                .filter(s => adminRegions.includes(s.regionId) && (regionFilter === 'all' || s.regionId === regionFilter))
                                .map(school => {
                                  const isPrimary = user.schoolId === school.id;
                                  const isServed = (user.schoolsServedIds || []).includes(school.id);
                                  const isChecked = isPrimary || isServed;
                                  
                                  return (
                                    <button
                                      key={school.id}
                                      onClick={() => toggleSchoolAssociation(user, school.id)}
                                      disabled={savingUid === user.uid}
                                      className={`w-full px-3 py-1.5 rounded-lg border text-[10px] font-bold tracking-tight transition-all duration-200 flex items-center justify-between uppercase font-mono ${
                                        isChecked 
                                          ? isPrimary 
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            : 'bg-cyan-50 text-cyan-700 border-cyan-200'
                                          : 'bg-white text-slate-400 border-slate-100 hover:bg-slate-50 hover:text-slate-600'
                                      }`}
                                    >
                                      <span className="truncate">{school.name}</span>
                                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isChecked ? 'bg-current border-transparent' : 'border-slate-200'}`}>
                                        {isChecked && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                    </button>
                                  );
                                })}
                            </div>
                          ) : user.role === 'organization' ? (
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-bold text-slate-800">{user.orgName || user.organizationName || 'No Company Link'}</span>
                                {isUserOrgLead(user, users) ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider">
                                    <Crown className="w-2.5 h-2.5" />
                                    LEAD
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-black uppercase tracking-wider">
                                    TEAM MEMBER
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-[10px] text-slate-400">Employer Profile</p>
                                {!isUserOrgLead(user, users) && (
                                  <button
                                    type="button"
                                    disabled={savingUid === user.uid}
                                    onClick={async () => {
                                      setSavingUid(user.uid);
                                      try {
                                        await setUserAsOrgLead(user.uid, true);
                                      } finally {
                                        setSavingUid(null);
                                      }
                                    }}
                                    className="text-[9px] font-black uppercase tracking-wider text-amber-700 hover:underline cursor-pointer"
                                  >
                                    • Make Org Lead
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span className="font-bold text-slate-800">{user.school || 'General District'}</span>
                              {user.grade && <p className="text-[10px] text-slate-400 mt-0.5">Grade Level: {user.grade}</p>}
                            </div>
                          )}
                        </td>

                        {/* Authorized regions (checkbox selectors) */}
                        <td className="py-4 px-6">
                          {['superadmin', 'regional_admin', 'organization'].includes(user.role) ? (
                            <div className="flex flex-wrap gap-2">
                              {allowedTenants.map(tenant => {
                                const isChecked = authorizedRegions.includes(tenant.id);
                                return (
                                  <button
                                    key={tenant.id}
                                    onClick={() => toggleRegionAssociation(user, tenant.id)}
                                    disabled={savingUid === user.uid}
                                    className={`px-2.5 py-1 rounded-xl border text-[10px] font-bold tracking-tight transition-all duration-200 flex items-center space-x-1 uppercase font-mono ${
                                      isChecked 
                                        ? 'bg-sky-50 text-sky-700 border-sky-200 shadow-xs' 
                                        : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50 hover:text-slate-600'
                                    }`}
                                  >
                                    {isChecked && <Check className="w-3 h-3 shrink-0" />}
                                    <span>{tenant.id.toUpperCase()}</span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-100 border border-slate-200 text-slate-600 font-mono uppercase">
                                {user.tenantId ? user.tenantId.toUpperCase() : 'GLOBAL'}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Security action */}
                        <td className="py-4 px-6 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedPasswordUser(user)}
                            className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-[#0094c8]/10 text-[#0094c8] border border-[#0094c8]/25 hover:bg-[#0094c8]/20 transition-all cursor-pointer"
                            title="Password management"
                          >
                            <Key className="w-3.5 h-3.5" />
                            <span>Reset Password</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {selectedPasswordUser && (
        <PasswordManagementModal
          user={selectedPasswordUser}
          isOpen={!!selectedPasswordUser}
          onClose={() => setSelectedPasswordUser(null)}
        />
      )}
    </div>
  );
};
